/**
 * DeciderSheet — record who won the super over, bowl-out or toss that
 * separates teams level on the table (when the rulebook says so). Two teams:
 * tap the winner. More: tap them in finishing order.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import RuleSheet from './RuleSheet';
import { rulebookApi, errorText } from './api';

const METHOD = { super_over: 'super over', bowl_out: 'bowl-out', toss: 'toss' };

export const deciderWord = (method) => METHOD[method] || 'tie-breaker';

export default function DeciderSheet({ open, eventId, pending, teams, onClose, onSaved }) {
  const [order, setOrder] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setOrder([]); setReason(''); setError(''); }, [pending]);
  if (!open || !pending) return null;

  // Level teams in table order
  const ids = [...pending.team_ids].sort((a, b) => (teams.find((t) => t.team_id === a)?.position ?? 0) - (teams.find((t) => t.team_id === b)?.position ?? 0));
  const name = (id) => teams.find((t) => t.team_id === id)?.team_name || 'Team';
  const two = ids.length === 2;
  const pick = (id) => {
    if (two) setOrder([id, ids.find((x) => x !== id)]);
    else setOrder((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  };
  const ready = order.length === ids.length;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await rulebookApi.addDecider(eventId, { team_ids: ids, method: pending.method, order, reason: reason.trim() || null });
      toast.success('Table updated');
      onSaved?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <RuleSheet open={open} onClose={onClose} testId="decider-sheet"
      title={two ? `Who won the ${deciderWord(pending.method)}?` : `${deciderWord(pending.method)[0].toUpperCase()}${deciderWord(pending.method).slice(1)} result`}
      subtitle={`${ids.map(name).join(' and ')} are level on points${pending.method === 'toss' ? '' : ' after the other tie-breakers'}.`}
      footer={(
        <button onClick={save} disabled={!ready || busy} className="w-full h-12 rounded-xl bg-[#2B3A67] text-white font-bold disabled:opacity-40" data-testid="decider-save">
          {busy ? 'Saving…' : 'Update the table'}
        </button>
      )}>
      {!two && <p className="text-xs text-gray-500 mb-2">Tap the teams in finishing order, best first.</p>}
      <div className="space-y-2">
        {ids.map((id) => {
          const pos = order.indexOf(id);
          const on = two ? order[0] === id : pos >= 0;
          return (
            <button key={id} onClick={() => pick(id)} data-testid={`decider-team-${id}`}
              className={`w-full h-12 rounded-xl border-2 px-4 flex items-center justify-between font-bold text-sm ${on ? 'border-[#3AAFA9] bg-[#E6F5F4] text-[#1F7A75]' : 'border-gray-200 text-gray-700'}`}>
              <span className="truncate">{name(id)}</span>
              {on && <span className="text-xs">{two ? 'Winner' : `#${pos + 1}`}</span>}
            </button>
          );
        })}
      </div>
      <p className="text-xs font-bold text-gray-500 mt-4 mb-1.5">Score or note <span className="font-normal">· optional</span></p>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="e.g. Super over 12-9"
        className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm" />
      {error && <p className="mt-3 text-sm text-[#B3263F] bg-[#FCE8EC] rounded-lg px-3 py-2">{error}</p>}
    </RuleSheet>
  );
}
